import React, { useEffect, useState } from "react";
import axios from "axios";
import { getCurrentUserId } from "../../awsAuth";
import "./FeedbackModal.scss";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  "http://localhost:5000";

const CLOSE_ANIMATION_MS = 220;

export default function FeedbackModal({ onClose }) {
  const [type, setType] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const SUPPORT_TELEGRAM_USERNAME =
  process.env.REACT_APP_SUPPORT_TELEGRAM_USERNAME || "cvanto_support";

  const isIdea = type === "idea";
  const isBug = type === "bug";

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEsc = (e) => {
      if (e.key === "Escape") {
        handleRequestClose();
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const handleRequestClose = () => {
    if (loading || isClosing) return;

    setIsClosing(true);

    window.setTimeout(() => {
      onClose?.();
    }, CLOSE_ANIMATION_MS);
  };

  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      handleRequestClose();
    }
  };

  const handleBack = () => {
    if (loading || isClosing) return;
    setType(null);
    setText("");
    setSent(false);
  };

  const openSupportTelegram = () => {
  const telegramUrl = `https://t.me/${SUPPORT_TELEGRAM_USERNAME}`;

    window.open(telegramUrl, "_blank", "noopener,noreferrer");
    handleRequestClose();
  };

  const handleSend = async () => {
    if (!text.trim() || !type || loading) return;

    try {
      setLoading(true);

      const userId = getCurrentUserId();

      await axios.post(`${API_BASE}/api/feedback`, {
        type,
        message: text.trim(),
        userId,
      });

      setSent(true);
      setText("");
    } catch (err) {
      console.error("Feedback error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (sent) return "Дякуємо 🙌🏻";
    if (isBug) return "Повідомити про проблему";
    if (isIdea) return "Що можна покращити у Cvanto?";
    return "Зворотний зв’язок";
  };

  const getDescription = () => {
    if (sent) {
      return "Дякуємо за вашу спостережливість 🫶🏻 Ми читаємо кожну пропозицію, уважно розглядаємо кожну проблему й поступово робимо Cvanto кращим.";
    }

    if (isBug) {
      return "З якою проблемою ви зіткнулися? Опишіть, будь ласка, що сталося, що працює не так або що заважає — ми якнайшвидше перевіримо і виправимо.";
    }

    if (isIdea) {
      return "Напишіть, чого вам не вистачає, що незручно або що варто додати.";
    }

    return "Оберіть, що саме ви хочете нам надіслати.";
  };

  const getPlaceholder = () => {
    if (isBug) return "Опишіть проблему коротко і зрозуміло...";
    if (isIdea) return "Напишіть вашу ідею...";
    return "Опишіть коротко...";
  };

  return (
    <div
      className={`feedback-overlay ${isClosing ? "is-closing" : ""}`}
      onMouseDown={handleOverlayMouseDown}
      onClick={handleOverlayMouseDown}
    >
      <div
        className={`feedback-modal ${isClosing ? "is-closing" : ""}`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="feedback-modal__topbar">
          {type && !sent ? (
            <button
              type="button"
              className="feedback-icon-button feedback-icon-button--left"
              onClick={handleBack}
              aria-label="Назад"
            >
              &lt;
            </button>
          ) : (
            <div className="feedback-icon-button feedback-icon-button--ghost" />
          )}

          <button
            type="button"
            className="feedback-icon-button feedback-icon-button--right"
            onClick={handleRequestClose}
            aria-label="Закрити"
          >
            ✕
          </button>
        </div>

        <div className="feedback-modal__content">
          {!type && !sent && (
            <>
              <h3 className="feedback-modal__title">{getTitle()}</h3>
              <p className="feedback-modal__description">{getDescription()}</p>

              <div className="feedback-options">
                <button
                  type="button"
                  className="feedback-option"
                  onClick={() => setType("idea")}
                >
                  <span className="feedback-option__emoji">💡</span>
                  <span className="feedback-option__text">
                    Запропонувати ідею
                  </span>
                </button>

                <button
                  type="button"
                  className="feedback-option"
                  onClick={() => setType("bug")}
                >
                  <span className="feedback-option__emoji">🐞</span>
                  <span className="feedback-option__text">
                    Повідомити про проблему
                  </span>
                </button>

                <button
                  type="button"
                  className="feedback-option feedback-option--support"
                  onClick={openSupportTelegram}
                >
                  <span className="feedback-option__emoji">👨🏻‍💻</span>
                  <span className="feedback-option__text">
                    Техпідтримка
                  </span>
                </button>
              </div>
            </>
          )}

          {type && !sent && (
            <>
              <h3 className="feedback-modal__title">{getTitle()}</h3>
              <p className="feedback-modal__description">{getDescription()}</p>

              <div className="feedback-textarea-wrap">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={getPlaceholder()}
                  maxLength={500}
                  className="feedback-textarea"
                />
                <div className="feedback-counter">{text.length}/500</div>
              </div>

              <button
                type="button"
                className="feedback-send"
                onClick={handleSend}
                disabled={!text.trim() || loading}
              >
                {loading ? "Надсилання..." : "Надіслати"}
              </button>
            </>
          )}

          {sent && (
            <div className="feedback-success">
              <div className="feedback-success__emoji">✨</div>
              <h3 className="feedback-modal__title">{getTitle()}</h3>
              <p className="feedback-modal__description">{getDescription()}</p>

              <button
                type="button"
                className="feedback-send feedback-send--success"
                onClick={handleRequestClose}
              >
                Гаразд
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}